import { Locale } from 'date-fns/locale';

export interface IError extends Error {
  status: number;
  statusMessage: string;
  code?: number;
  message: string;
}

export type LocaleKeys = 'pt-BR' | 'en' | 'es';

export type Locales = {
  [K in LocaleKeys]?: Locale;
};

export type WeekDays = 'SUN' | 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT';

export enum WeekDaysEnum {
  SUN = 'SUN',
  MON = 'MON',
  TUE = 'TUE',
  WED = 'WED',
  THU = 'THU',
  FRI = 'FRI',
  SAT = 'SAT',
}

export type Price = {
  decimal: number;
  cents: number;
};

export type TicketType = {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  privateTicketOffice: boolean;
  id: string;
  name: string;
  price: Price;
  fee: Price;
  color: string;
  description: string | null;
  nubankBIN: string | null;
};

export type TicketTypeWithOptionalTicketTypes = TicketType & { TicketTypes?: TicketType };

export type AssignedTicket = {
  ticket: TicketType;
  fee: Price;
  price: Price;
};

export type AssignedTicketWithFee = TicketType & {
  fee: Price;
  TicketTypes?: any;
};

export type Session = {
  available: boolean;
  startsAt: number;
  endsAt: number;
  assignedTickets: AssignedTicketWithFee[];
  relatedSessionId?: { assignedTickets: AssignedTicketWithFee[] };
  configurationId?: string;
  overlap?: boolean;
  totalTicketsAvailable?: number;
  durationInMinutes?: number;
  isSoldOut?: boolean;
};

export enum EnumClassroomType {
  ONLINE = 'ONLINE',
  IN_PERSON = 'IN_PERSON',
}

export type Producer = {
  id: string;
  name: string;
  cnpj: string;
  zipCode?: string | null;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  complement?: string | null;
  bankName?: string | null;
  bankAgency?: string | null;
  bankAccount?: string | null;
  bankAccountType?: string | null;
  bankPixKey?: string | null;
  bankHolder?: string | null;
  bankHolderDoc?: string | null;
};

export type Experience = {
  length: number;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  slug: string;
  startDate: number;
  endDate: number;
  isMain: boolean;
  summary: string;
  description: string;
  canSellUntilDaysAdvance: number;
  saleAnticipationDays: number;
  maxCanBePurchasedInDay: number;
  maxCanBePurchasedInExperience: number;
  minCanBePurchasedInExperience: number;
  status: 'VISIBLE' | 'HIDDEN' | 'PRIVATE';
  excludedDates: string[];
  startOfSalesDate: string;
  image: string;
  onTextImages: string[];
  parentalRating: ParentalRating;
  feeType: FeeTypes | null;
  overlapFee: boolean | null;
  valueOverlapFee: number | null;
  websiteLink?: string;
  scholarshipHolderLink?: string;
  courseLink?: string;
  passwordClassroom?: string;
  typeClassroom?: EnumClassroomType;
  experiencesTypeId: string;
  experiencesType: ExperiencesType;
  configurations: ExperienceConfiguration[];
  maxInstallments: number;
  installments: boolean;
  ExperiencesType?: ExperiencesType;
  producerId?: string;
  producer?: Producer;
};

export type ExperienceConfiguration = {
  id: string;
  experienceId: string;
  name: string;
  affectedWeekDays: WeekDaysEnum[];
  durationInMinutes: number;
  endTimeInMinutes: number;
  startTimeInMinutes: number;
  ticketsAmountIntended: number;
  startDate: Date;
  endDate: Date;
  assignedTickets: {
    experienceConfigurationId: string;
    ticketTypeId: string;
    totalIntended: number;
    order: number;
    assignedAt: Date;
  }[];
  TicketsOnExperienceConfigurations?: {
    experienceConfigurationId: string;
    ticketTypeId: string;
    totalIntended: number;
    order: number;
    assignedAt: Date;
  }[];
  createdAt: string;
  sessions: Session[];
  updatedAt: string;
  overlap: boolean;
  excludedDates: string[];
  overlapDates: string[];
};

export type TicketTypeCart = {
  id: string;
  name: string;
  price: number;
  fee: number;
};

export type SessionCart = {
  startsAt: number;
  endsAt: number;
};

export type ExperienceCart = {
  id: string;
  name: string;
  image: string;
  maxInstallments: number;
  installments: boolean;
  experiencesType: Experience['experiencesType'];
  minCanBePurchasedInExperience: number;
  maxCanBePurchasedInDay: number;
};

export interface Ticket {
  id: string;
  amount: number;
  price: number;
  fee: number;
  name: string;
}

export interface Order {
  experience: ExperienceCart;
  startDate: number;
  endDate: number;
  tickets: Ticket[];
  configurationId: string;
  selectedSeats?: string[];
  selectedSeatsIds?: string[];
}

export enum ParentalRating {
  FREE = 'FREE',
  UNDER_10 = 'UNDER_10',
  UNDER_12 = 'UNDER_12',
  UNDER_14 = 'UNDER_14',
  UNDER_16 = 'UNDER_16',
  UNDER_18 = 'UNDER_18',
  CHECK_AT_THE_ENTRANCE = 'CHECK_AT_THE_ENTRANCE',
}

export enum TicketTypeColors {
  LIGHT_BLUE = 'LIGHT_BLUE',
  LIGHT_GREEN = 'LIGHT_GREEN',
  LIGHT_PURPLE = 'LIGHT_PURPLE',
  PINK = 'PINK',
  ORANGE = 'ORANGE',
  AQUA = 'AQUA',
}

export type SystemWallet = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  userId: string;
  active: boolean;
  numberCardInt: string;
  WalletSubscription: {
    deletedAt: Date;
    planId: string;
    nextBillingDate?: string | null;
    isCanceled?: boolean;
  }[];
};

export type User = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  role: string;
  email: string | null;
  name: string | null;
  phone: string;
  active: boolean;
  SystemWallet?: SystemWallet & { qrCode: string };
  dependentOn?: string;
  cognitoId?: string;
  password?: string;
  birthDate?: Date | null;
  dependentCode?: string;
  optinText?: string;
  optinAcceptanceTerm?: boolean;
  isChildrenPartner?: boolean;
  convenienceFee?: boolean;
  subscription?: { planId: string };
  documentNumber?: string;
  documentNumberHash?: string;
  typeUserPlan?: TypeUserPlan;
  planType?: EnumMaspType;
  effectivePlan?: EffectiveUserPlan;
};

export type EffectiveUserPlanStatus = 'active' | 'expired' | 'canceled' | 'no_plan' | 'titular_inactive';

export type EffectiveUserPlan = {
  planId: string | null;
  title?: string;
  benefits?: string;
  description?: string;
  addDependents?: boolean;
  addDependentsCount?: number;
  nextBillingDate?: string | null;
  status: EffectiveUserPlanStatus;
  isDependent: boolean;
  titularUserId: string | null;
};

export enum SaleStatus {
  PAYMENT_IN_PROGRESS = 'PAYMENT_IN_PROGRESS',
  PAYMENT_COMPLETED = 'PAYMENT_COMPLETED',
  PAYMENT_ERROR = 'PAYMENT_ERROR',
  PAYMENT_REFUNDED = 'PAYMENT_REFUNDED',
  PAYMENT_EXPIRED = 'PAYMENT_EXPIRED',
  PAYMENT_NOT_AUTHORIZED = 'PAYMENT_NOT_AUTHORIZED',
}

export enum UserTicketStatus {
  WAITING_PAYMENT = 'WAITING_PAYMENT',
  PAYMENT_COMPLETED = 'PAYMENT_COMPLETED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  PAYMENT_REFUNDED = 'PAYMENT_REFUNDED',
}

export enum SaleType {
  TICKET_PURCHASE = 'TICKET_PURCHASE',
  DONATION = 'DONATION',
}

export enum SalePaymentMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  PIX = 'PIX',
  CASH = 'CASH',
  BANK_DEPOSIT = 'BANK_DEPOSIT',
  FREE = 'FREE',
  BONIFICADO = 'BONIFICADO',
}

export type Sale = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  parentId: string | null;
  paymentMethod: SalePaymentMethod;
  paymentString: string;
  price: Price;
  status: SaleStatus;
  trackingId: string;
  type: SaleType;
  userId: string;
  donation?: Sale & { type: SaleType.DONATION };
};

export type UserTicket = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  wasSpent: boolean;
  status: string;
  ticketTypeId: string;
  saleId: string;
  sessionId: string;
  experienceId: string;
  evaluatorId: string | null;
  evaluatedAt: string | null;
  DiscountCoupon?: { id: string; code: string; description: string; percentage: number; discount: number };
};

export type UserOrders = {
  id: string;
  createdAt: string;
  paymentMethod: SalePaymentMethod;
  paymentString: string;
  price: Price;
  status: SaleStatus;
  trackingId: string;
  donations?: Sale['donation'][];
  tickets: {
    id: string;
    wasSpent: boolean;
    status: string;
    session: {
      startDate: string;
      endDate: string;
    };
    experience: {
      name: string;
      image: string;
    };
    soldFor: Price;
    saleFee: Price;
    isCancelable: boolean;
  }[];
};

export enum UserGroups {
  USER = 'users',
  STAFF = 'staff',
  ADMIN = 'admin',
  BILHETEIRO = 'bilheteiro',
  LIDER_BILHETERIA = 'lider_bilheteria',
  OPERACOES = 'operacoes',
}

export enum FeeTypes {
  FIXED_VALUE = 'FIXED_VALUE',
  PERCENTAGE_VALUE = 'PERCENTAGE_VALUE',
}

export enum PurchaseTypes {
  ANONYMOUS = 'ANONYMOUS',
  REGISTER = 'REGISTER',
}

export interface IReportTicket {
  statusSale: any;
  created_at?: any;
  numberInstallment?: number | null;
  saleId?: string | null;
  paymentMethod?: string | null;
  nature?: number | null;
  emission?: Date | string | null;
  amount?: string | number | null;
  coin?: number | null;
  nsu?: string | number | null;
  txId?: string | number | null;
  pixAuthorization?: string | number | null;
  constCenter?: string | null;
  accountingItem?: string | null;
  valueClass?: string | null;
  tickets?: {
    name?: string | null;
    eventStart?: Date | null;
    ticketType?: string | null;
    saleId?: string | null;
    checkinDate?: Date | null;
    used?: boolean | null;
    ticketStatus?: string | null;
    eventEnd?: Date | null;
    eventName?: string | null;
    soldPrice?: string | null;
    fee?: string | null;
  }[];
  donations?: string | number | null | Array<{ amount: string | number | null; type: string | number | null }>;
  canceledAt?: Date | null;
}

export interface IReportAmigoMasp {
  numberInstallment?: number | null;
  sale_id?: string | number | null;
  paymentMethod?: string | null;
  nature?: number | null;
  emission?: string | null;
  amount?: number | null;
  coin?: number | null;
  nsu?: string | number | null;
  txId?: string | number | null;
  pixAuthorization?: string | null;
  constCenter?: string | null;
  accountingItem?: string | null;
  valueClass?: string | null;
  statusSale?: string | null;
  event_name?: string | null;
  event_start?: string | null;
  event_end?: string | null;
  client?: string | null;
  ticket_type?: string | null;
  checkin_date?: string | null;
  used?: string | null;
  ticket_status?: string | null;
  sold_price?: number | null;
  fee?: number | null;
  donation?: number | null;
  discount?: number | null;
  ticket_price?: number | null;
  ticket_id?: string | number | null;
  installments?: number | null;
}

export interface IConfiguration {
  configurationId: {
    id: string;
    startDate: string | Date;
    endDate: string | Date;
    overlap: boolean;
    excludedDates: string[];
    overlapDates: string[];
  }[];
}

export enum EnumExperienceType {
  MASP_DOE = 'MASP_DOE',
  BILHETERIA = 'BILHETERIA',
  MASP_AMIGO = 'MASP_AMIGO',
  MASP_CURSOS = 'MASP_CURSOS',
  MASP_EVENTOS = 'MASP_EVENTOS',
  MASP_OFICINAS = 'MASP_OFICINAS',
  MASP_PESQUISA = 'MASP_PESQUISA',
  MASP_PATRONOS = 'MASP_PATRONOS',
  MASP_PALESTRAS = 'MASP_PALESTRAS',
  MASP_SEMINARIOS = 'MASP_SEMINARIOS',
  MASP_EVENTO_AMIGO = 'MASP_EVENTO_AMIGO',
  MASP_EMPRESA_AMIGA = 'MASP_EMPRESA_AMIGA',
  MASP_ESCOLA_PUBLICA = 'MASP_ESCOLA_PUBLICA',
  MASP_ESCOLA_PARTICULAR = 'MASP_ESCOLA_PARTICULAR',
  MASP_MEDIACAO_PROFESSORES = 'MASP_MEDIACAO_PROFESSORES',
  MASP_PROGRAMA_DE_GRATUIDADE = 'MASP_PROGRAMA_DE_GRATUIDADE',
  MASP_ESPETACULOS_E_EVENTOS_SEM_LUGAR = 'MASP_ESPETACULOS_E_EVENTOS_SEM_LUGAR',
  MASP_ESPETACULOS_E_EVENTOS_COM_LUGAR = 'MASP_ESPETACULOS_E_EVENTOS_COM_LUGAR',
  MASP_FESTAS = 'MASP_FESTAS',
  MASP_FESTAS_PISTA = 'MASP_FESTAS_PISTA',
  VERNISSAGE = 'VERNISSAGE',
  PRELUDE = 'PRELUDE',
  MASP_VISITA_GUIADA_PATROCINADOR = 'MASP_VISITA_GUIADA_PATROCINADOR',
  PRE_COLLEGE_MASP = 'PRE_COLLEGE_MASP',
  MASP_VISITA_MEDIADA = 'MASP_VISITA_MEDIADA',
  MASP_VISITA_MEDIADA_GRUPO_EXCLUSIVO = 'MASP_VISITA_MEDIADA_GRUPO_EXCLUSIVO',
}

export interface ExperiencesType {
  id: string;
  name: EnumExperienceType;
  description: string;
  color: string;
  order?: number;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface ITicketData {
  id: string;
  wasSpent: boolean;
  status: string;
  ticketTypeId: string;
  saleId: string;
  experienceId: string;
  experienceName: string;
  sessionId: string;
  evaluatedAt: string | null;
  evaluatorId: string | null;
  salefee: number;
  soldfor: number;
  operatorId: string | null;
  operatorEmail: string | null;
  purchaseAnonymous: boolean;
  userId: string | null;
  operatorBuyerEmail: string;
  operatorBuyerId: string;
  bankDiscount: boolean;
  discountCouponId: string | null;
  createdAtDate: string;
  createdAtTime: string;
  updatedAtDate: string;
  updatedAtTime: string;
  checkInDate: string | null;
  checkInTime: string | null;
  'Tipo de Ingresso': string;
  'inicio do evento': string;
  'hora inicio do evento': string;
  'fim do evento': string;
  'hora fim do evento': string;
  phone: string | null;
  email: string | null;
  token: string | null;
  description: string | null;
}

export interface ITicketType {
  id: string;
  name: string;
  price: number;
  color: string;
  privateTicketOffice: boolean;
  description: string | null;
  nubankBIN?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
}

export enum LocationType {
  VAO_LINA = 'VAO_LINA',
  LINA = 'LINA',
  PIETRO = 'PIETRO',
  GUARDA_VOLUMES_LINA = 'GUARDA_VOLUMES_LINA',
  GUARDA_VOLUMES_PIETRO = 'GUARDA_VOLUMES_PIETRO',
  LOJA_MASP = 'LOJA_MASP',
}

export interface Location {
  id: string;
  name: LocationType;
  status: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export enum EnumPlataform {
  webADM = 'webADM',
  mobileCheckin = 'mobileCheckin',
}

export interface IPlan {
  id: string;
  benefits: string;
  price: number;
  description: string;
  title: string;
  experiencesTypeId: string;
  maxInstallments: number;
  planTypeId: string;
  feeType: FeeTypes;
  billingPeriod?: EnumBillingPeriod;
  feeValue: number;
  active: boolean;
  addDependents?: boolean;
  dependentPlanId?: string | null;
  planType?: IPlanType;
}

export interface IPlanDiscountCoupon {
  id: string;
  code: string;
  description: string | null;
  discount: number;
  renewalPercentageValue: number;
  activationPercentageValue: number;
  quantity: number;
  usedQuantity: number;
  expireAt: string;
  active: boolean;
  limitForUser: number;
  plans?: IPlanDiscountCouponToPlan[];
}

export interface IPlanDiscountCouponToPlan {
  planId: string;
  discountCouponId: string;

  PlanDiscountCoupon?: IPlanDiscountCoupon;
  Plan?: IPlan;

  createdAt: string;
  updatedAt: string;
}

export enum EnumPlanType {
  GOVERNANCE = 'GOVERNANCE',
  AMIGO_MASP = 'AMIGO_MASP',
  EMPRESA_AMIGA = 'EMPRESA_AMIGA',
  MASP_PATRONOS = 'MASP_PATRONOS',
}

export interface IPlanType {
  id: string;
  name: string;
  maspType: EnumPlanType;
  description: string;
  createAt: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface IUserAddress {
  id?: string;
  userId?: string;
  street: string;
  number?: string | null;
  complement?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  createdAt: string;
  updatedAt: string | null;
  deletedAt?: string | null;
}

export enum EnumMaspType {
  MASP_PATRONOS = 'MASP_PATRONOS',
  EMPRESA_AMIGA = 'EMPRESA_AMIGA',
  GOVERNANCE = 'GOVERNANCE',
  AMIGO_MASP = 'AMIGO_MASP',
}

export enum EnumBillingPeriod {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
  BIMONTHLY = 'BIMONTHLY',
  QUARTERLY = 'QUARTERLY',
  SEMIANNUAL = 'SEMIANNUAL',
  WEEKLY = 'WEEKLY',
  DAILY = 'DAILY',
}

export interface ISubscriber {
  id: string;
  rootSubscriptionId: string;
  subscriptionId: string;
  subscriber: string;
  trackingId: string | null;
  email: string;
  canceledAt: string | null;
  status: string;
  nextBillingDate: string | null;
  feeValue: number | null;
  priceWithInstallments: number | null;
  plan: {
    id: string;
    price: number;
    title: string;
    planType: {
      id: string;
      name: string;
    };
  };
  cpf: string;
  phone: string;
  installments: number;
  accessionDate: string;
  renewalHistory: any[];
  cancellationHistory: any[];
  title: string;
  planType: string;
  birthDate: string | null;
}

// Tipo de usuário no plano
// DEPENDENT: Dependente do usuário
// RESPONSIBLE: Responsável pelo dependente
// NORMALPLAN: nem responsável nem dependente, serve para caso de planos que não permitem dependentes
export enum TypeUserPlan {
  DEPENDENT = 'DEPENDENT',
  RESPONSIBLE = 'RESPONSIBLE',
  NORMALPLAN = 'NORMALPLAN',
}
// Push Notifications
export enum NotificationStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum NotificationTargetType {
  TOPIC = 'TOPIC',
  USER = 'USER',
  ALL_USERS = 'ALL_USERS',
  ROLE = 'ROLE',
  PLAN = 'PLAN',
  EXPERIENCE = 'EXPERIENCE',
  USERS = 'USERS',
}

export type NotificationPlatform = 'web' | 'mobile';

export interface CreateNotificationInput {
  title: string;
  message: string;
  scheduledAt: Date;
  enabled?: boolean;
  createdBy: string;
  targetType?: NotificationTargetType;
  topic?: string;
  targetUserId?: string;
  tokens?: string[];
  data?: Record<string, any>;
  imageUrl?: string;
  platforms?: NotificationPlatform[];
}

export interface PushNotification {
  id: string;
  title: string;
  message: string;
  scheduledAt: Date;
  enabled: boolean;
  createdBy: string;
  targetType: NotificationTargetType;
  status: NotificationStatus;
  retryCount: number;
  errorMessage?: string;
  fcmResponse?: Record<string, any>;
  topic?: string;
  targetUserId?: string;
  tokens?: string[];
  data?: Record<string, any>;
  imageUrl?: string;
  platforms?: NotificationPlatform[];
  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
}

// Plan Voucher interface
export interface IPlanVoucherWithDetails {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  purchasedBy: string;
  installments: number | null;
  priceWithInstallments: number | null;
  trackingId: string;
  tId: string | null;
  paymentString: string | null;
  nsu: string | null;
  paymentStatus: string;
  paymentMethod: string;
  discountCouponId: string | null;
  discountPercentage: number | null;
  tokenURLId: string | null;
  planId: string;
  redeemCode: string;
  redeemedAt: Date | null;
  redeemedBy: string | null;
  maxiPagoOrderId: string | null;
  maxiPagoClientId: string | null;
  maxiPagoTokenCard: string | null;
  // Relacionamentos
  purchaser: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
  redeemer?: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  } | null;
  plan: {
    id: string;
    title: string;
    planType: {
      id: string;
      title: string;
    };
  };
}

export interface IPlanVoucherResponse {
  data: IPlanVoucherWithDetails[];
  total: number;
  page: number;
  totalPages: number;
}

export enum SaleSubscriptionStatus {
  PAYMENT_COMPLETED = 'PAYMENT_COMPLETED',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  PAYMENT_ERROR = 'PAYMENT_ERROR',
}

export type AuthMethod = 'sms' | 'email' | 'whatsapp' | 'dependent_code';

export interface IDiscountCoupon {
  id: string;
  code: string;
  discount: number;
  percentage: number;
  description: string;
  quantity: number;
  usedQuantity: number;
  expireAt: Date;
  active: boolean;
  experienceId?: string;
  experienceIds?: string[];
  experienceTypeId?: string;
  allowedEmailDomains?: string[];
  allowedPlanIds?: string[];
  allowedPlanTypeIds?: string[];
  limitForUser: number;
  tickets: {
    id: string;
    name: string;
    color: string;
    price: number;
  }[];
}
